import { Document, Page, Text, View, StyleSheet } from 'external/pdf';
import { FC, PropsWithChildren, useMemo, Fragment } from 'external/react';
import type { NewPlan } from '../../plan-rd/index'
import type { DailyPlanning } from '../../plan-rd/DailyPlanning/DailyPlanning'
import type { UnitPlan } from '../../plan-rd/UnitPlan/hooks/useUnitPlan'
import dayjs from 'dayjs';
import { isEmptyOrWhiteSpace } from 'helpers/utils';
import moment from 'moment';

const styles = StyleSheet.create({
  page: {
    padding: 10,
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.5,
    backgroundColor: '#fff',
  },
  sectionHeader: {
    backgroundColor: '#2c75bd',
    padding: 10,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 13,
    color: '#fff',
  },
  sectionTitle: {
    flexGrow: 1,
  },
  rightTitle: {
    flexShrink: 0,
  },
  sectionContent: {
    backgroundColor: '#fff',
    padding: 10,
    width: '100%',
    display: 'flex',
    // alignItems: 'center',
    // minHeight: '100px',
    flexDirection: 'column',
    // justifyContent: 'space-between',
    // flex: 1,
  },
  mainContainer: {
    flexDirection: 'column',
    borderWidth: 2,
    borderColor: '#2c75bd',
    padding: 0,
  },
  title: {
    fontSize: 10.5,
    color: '#2c75bd',
  },
  text: {
    fontSize: 12,
    color: '#787b7b',
    marginTop: 3,
    marginBottom: 0,
    marginLeft: 5,
  },
  subsection: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    // flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
    backgroundColor: '#fff',
    width: '100%',
  },
  value: {},
  section: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    // padding: 10,
    backgroundColor: '#fff',
    width: '100%',
    display: 'flex',
    // flex: 1,
    height: 'auto'
    // height: 'fit-content',
    // minHeight: '150px',
  },
  strategyHeader: {
    backgroundColor: '#fff',
    border: 'solid 1px #2c75bd',
    marginTop: 10,
    // padding: 10,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 13,
    color: '#2c75bd',
  },

});


type PdfRdProps = {
  newPlan: NewPlan
  translate: (key: string) => string
  profile: any
  subjects: any
  grades: any
}

type DailyPlanningPDFProps = {
  translate: (key: string) => string
  content: DailyPlanning
}

const keyTranslations = {
  'dailyPlanning': 'Planificación diaria',
  'teacher': 'teacher',
  'learningUnit': 'Unidad de aprendizaje',
  'unitPlan': 'Plan de unidad',
  'communicative': 'Competencia comunicativa',
}

function filterDailyStrategy (props:any) {
  const [_, value = {}] = props = []
  return !Object.values(value).every(value => isEmptyOrWhiteSpace(value))
}

const DailyPlanningPDF: FC<DailyPlanningPDFProps> = props => {
  const { translate, content } = props
  const strategies = Array.isArray(content?.strategies) ? content.strategies : []
  return (
    <View style={{...styles.subsection, padding: '5px 0 0 10px'}}>
      {strategies.filter(Boolean).map(({ strategy, phase }, indexX) => (<Strategy key={indexX} indexX={indexX} translate={translate} strategy={strategy} phase={phase}/>))}
    </View>
  )
}

function Strategy (props: any) {
  const {strategy, phase, translate, indexX} = props
  const order = ['start', 'development', 'end'];
  
  const phases = phase && typeof phase === 'object' && !Array.isArray(phase) ? Object.entries(phase) : []
  phases.sort(([keyA],[keyB]) => { return order.indexOf(keyA) - order.indexOf(keyB); })

  const shouldRenderPhases = phases.length > 0

  const shouldRenderName = !isEmptyOrWhiteSpace(strategy)

  const shouldRender = shouldRenderName || shouldRenderPhases

  return shouldRender ? (
    <View>
      {shouldRenderName && <Text style={styles.strategyHeader}>{`Estrategia ${indexX + 1}: ${String(strategy)}`}</Text>}
      {/* {shouldRenderName && (<Text style={styles.strategyName}>{String(strategy)}</Text>)} */}
      {shouldRenderPhases && (<> 
        <Text style={{...styles.title, padding: '5px 0 0 10px', fontSize: 12}}>{translate('Fases')}:</Text>
        { phases.map(props => {
          const [key, value] = props
          const phaseValues = value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value) : []
          const valuesToRender = phaseValues.filter(([_, valuePhase]) => typeof valuePhase === 'string' && !isEmptyOrWhiteSpace(valuePhase))
          return valuesToRender.length > 0 && (
            <>
            <Text style={{...styles.title, padding: '5px 0 0 30px'}}>{translate(key)}:</Text>
            {valuesToRender.map(([keyPhase, valuePhase], index) => (<Phase key={index} translate={translate} keyPhase={keyPhase} valuePhase = {valuePhase}/>))}
            </>)
        })}
      </>)}
    </View>
  )
  : null
}

function Phase (props: any) {
  const {translate, keyPhase, valuePhase} = props
  return (<View wrap={false}>
    <Text style={{...styles.title, padding: '5px 0 0 40px'}}>{translate(keyPhase)}:</Text>
    <Text style={{...styles.text, paddingLeft: '40px'}}>{translate(valuePhase)}</Text>
  </View>)
}


const UnitPlanPDF: FC<{ translate: (key: string) => string, content: UnitPlan }> = props => {
  const { translate, content = {} } = props
  const order = ['cicleSkill', 'gradeSkill', "communicative" , "ethical" , "logicalThinking" , "problemSolving" , "scientific" , "environmental" , "personalDevelopment"];
  return (
    <>
      {Object.entries(content || {}).sort(([keyA],[keyB]) => { return order.indexOf(keyA) - order.indexOf(keyB);}).map(([key, value], index) => (
        <View style={{...styles.subsection, marginTop: '10px'}} key={index} wrap={false}>
          <Text style={{...styles.title, fontSize: 13}}>{keyTranslations[translate(key) as keyof typeof keyTranslations] ?? key}:</Text>
          {typeof value === 'string' ? (
            <Text style={styles.text}>{String(value)}</Text>
          ) : (
            Object.entries(value && typeof value === 'object' ? value : {}).map(([keySkill, valueSkill]) => {
              return (
                <View style={{...styles.subsection}} key={keySkill}>
                  <Text style={{...styles.title, fontSize: 11, paddingLeft: '10px'}}>{keySkill}:</Text>
                  <Text style={{...styles.text, paddingLeft: '10px'}}>{String(valueSkill)}</Text>
                </View>
              )
            })
          )}
          {/* {renderContentByType(value)} */}
        </View>
      ))}
    </>
  )
}

const shouldRenderByType = (content:any): boolean => {
  switch (typeof content) {
    case 'string': return !isEmptyOrWhiteSpace(content)
    case 'object': return content !== null && Object.entries(content).length > 0
    default: return false;
  }
}

function shouldRenderSubSectionByType(props: PropsWithChildren<{ keyS: string; value: string; }>) {
  switch (typeof props.value) {
    case 'string': return !isEmptyOrWhiteSpace(props.value)
    default: return false;
  }
}


const renderContentByType = (content: any): React.ReactNode | null => {
  if (typeof content === 'string') {
    return <Text style={styles.text}>{content.toUpperCase()}</Text>
  } else if (Array.isArray(content)) {
    return content.map((item) => {
      return(
        typeof item === 'object' ? (
          Object.entries(item).map(([k, v]) => (
            <Text key={k} style={styles.value}>
              {k}: {String(v)}
            </Text>
          ))
        ) : (
          <Text>{String(item)}</Text>
        )
      )
    }
    )
  } else if (content !== null && typeof content === 'object') {
    return Object.entries(content).map(([key, value]) => {
      return (
        <>
          <Text key={key} style={styles.text}>{String(key)}</Text>
          {renderContentByType(value)}
        </>
      )
    })
  }
  return null;
}

const PdfSubSection: FC<{ keyS: string, value: string }> = props => {
  const { keyS, value } = props

  const display = useMemo(() => (shouldRenderSubSectionByType(props) ? {} : {'display': 'none'}), [props])

  return (
    <View style={{...styles.subsection, ...display}}>
      <Text style={styles.title}>{keyS}:</Text>
      <Text style={styles.text}>{value}</Text>
    </View>
  )
}

const keyNameTranslations = {
  'dailyPlanning': DailyPlanningPDF,
  'unitPlan': UnitPlanPDF,
}

type PDFSectionProps = PDFSectionBaseProps & ({
  content: {[key: string]: string}
  shouldBreak?: boolean;
  keyName: 'unitPlan'
} | {
  content: DailyPlanning
  shouldBreak?: boolean;
  keyName: 'dailyPlanning'
} | {
  content: string
  shouldBreak?: boolean;
  keyName: string
})


type PDFSectionBaseProps = {
  sectionTitle: string
  rightTitle?: string
  translate: (key: string) => string
}

const PdfSection: FC<PDFSectionProps> = ({ sectionTitle, content, keyName, translate, rightTitle, shouldBreak }) => {
  const ContentComponent = keyNameTranslations[keyName as keyof typeof keyNameTranslations]
  const display = useMemo(() => (shouldRenderByType(content) ? {} : {'display': 'none'}), [content])
  return (
    <View style={{...styles.section, ...display}} break={shouldBreak}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{sectionTitle}</Text>
        {rightTitle && (<Text style={styles.rightTitle}>{rightTitle}</Text>)}
      </View>
      <View style={styles.sectionContent}>
        {ContentComponent ? <ContentComponent translate={translate} content={content as any} /> : renderContentByType(content)}
        {/* {renderContentByType(content)} */}
      </View>
    </View>
  )
}

const PdfHeader: FC<{ values: { [key: string]: string } }> = ({ values }) => {
  return (
    <View style={styles.header}>
      {Object.keys(values).map((value, index) => (
        <PdfSubSection
          key={index}
          value={values[value]}
          keyS={value}
        />
      ))}
    </View>
  )
}

const RdPreviewPDFDocument: FC<PdfRdProps> = ({ newPlan, translate, profile }) => {
  const { finishTime, startTime, title, subject, unit, content } = newPlan

  const dailyPlans = Array.isArray(newPlan?.content?.dailyPlanning)
    ? [...newPlan.content.dailyPlanning] as DailyPlanning[]
    : []

  dailyPlans.sort((a, b) => moment(a.date).isBefore(moment(b.date)) ? -1 : 1)

  const dailyPlanSections = dailyPlans.map((props, index) => ({
    content: props,
    title: `${keyTranslations.dailyPlanning} ${index + 1}`,
    rightTitle: props.date
  }))


  return(
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.mainContainer}>
          <PdfHeader
            values={{
              [translate('school')]: profile?.institution.name,
              [translate('teacher')]: `${profile?.individual.last_name}, ${profile?.individual.first_name}`
            }}
          />
          <View style={{...styles.section}} >
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{translate('Detalles del contenido')}</Text>
            </View>
            <View style={styles.sectionContent}>
              {Object.entries({
                title, 
                startTime: dayjs(startTime).format('YYYY/MM/DD [a las] hh:mm a'), 
                finishTime: dayjs(finishTime).format('YYYY/MM/DD [a las] hh:mm a'), 
                subject, 
                unit,
                estimatedTime: content?.estimatedTime || '',
                learningSituation: content?.learningSituation || '',
                thematicAxis: content?.thematicAxis || '',
                teacher: content?.teacher || '',
                section: content?.section || '',
              }).filter(([_, value]) => value !== '').map(([key, value], index) => (
                <PdfSubSection
                  key={index}
                  keyS={key}
                  value={String(value)}
                />
              ))}
            </View>
          </View>
          <PdfSection
            keyName='unitPlan'
            // shouldBreak
            sectionTitle={translate(keyTranslations.unitPlan)}
            content={newPlan?.content?.unitPlan}
            translate={translate}
          />
          {dailyPlanSections.map((props, index) => (
            <PdfSection
             key={index}
             keyName='dailyPlanning'
             translate={translate}
             sectionTitle={props.title}
             rightTitle={props.rightTitle}
             content={props.content}
            />
            ))}
        </View>
      </Page>
    </Document>
  )
};

export default RdPreviewPDFDocument;

